import {
  faExclamationTriangle,
  faSnooze,
} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {FC} from 'react';
import {useInterval, useLocalStorage} from '@mantine/hooks';
import {Env} from '../config/env-vars';
import {DateTime} from 'luxon';
import {ActionIcon, Group, Menu} from '@mantine/core';
import {ClassNames, css} from '@emotion/react';

const envLabelMap = {
  local: 'LOCAL',
  development: 'TEST',
  stage: 'STAGING',
};

export type SnoozeNonProductionWarning = {
  hideProductionWarning: boolean;
  snoozeEndDate?: Date;
};

const snoozeTimes = [15, 30, 45, 60];
export const snoozeNonProductionWarningCacheKey =
  'non-production-warning-banner';
const snoozeNonProductionWarningInitialValues: SnoozeNonProductionWarning = {
  hideProductionWarning: false,
};

export const envTag: string = envLabelMap[Env.name || ''];

type NonProductionWarningProps = {
  show?: boolean;
  setSnooze?: (snoozeDurationMinutes: number) => void;
};
export const NonProductionWarning: FC<NonProductionWarningProps> = ({}) => {
  const [snooze, setSnooze, clearSnooze] =
    useLocalStorage<SnoozeNonProductionWarning>({
      key: snoozeNonProductionWarningCacheKey,
      defaultValue: snoozeNonProductionWarningInitialValues,
    });

  useInterval(() => {
    if (snooze?.snoozeEndDate && new Date(snooze.snoozeEndDate) <= new Date()) {
      clearSnooze();
    }
  }, 5000);

  const handleSetSnooze = (snoozeAmountMinutes: number) => {
    setSnooze({
      hideProductionWarning: true,
      snoozeEndDate: DateTime.now()
        .plus({minutes: snoozeAmountMinutes})
        .toJSDate(),
    });
  };

  return envTag ? (
    <>
      {!snooze?.hideProductionWarning && (
        <div css={styles}>
          <ClassNames>
            {({cx}) => (
              <Group
                className={cx('environment-warning', Env.name)}
                justify="space-between"
              >
                <div></div>
                <span>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  &nbsp; You are on the {envTag} environment.
                </span>
                <Menu
                  trigger="click"
                  width={100}
                  offset={0}
                  shadow="md"
                  withinPortal
                  position="top"
                  zIndex={2000}
                >
                  <Menu.Target>
                    <ActionIcon variant="transparent" color={'gray.1'}>
                      <FontAwesomeIcon icon={faSnooze} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {snoozeTimes.map((snoozeTime, index) => (
                      <Menu.Item
                        key={index}
                        onClick={() => handleSetSnooze(snoozeTime)}
                      >{`${snoozeTime} mins`}</Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
              </Group>
            )}
          </ClassNames>
        </div>
      )}
    </>
  ) : null;
};

const styles = css`
  height: 30px;

  .environment-warning {
    text-align: center;
    padding: 3px 0 2px;

    &.local {
      background-color: #000;
      color: #fff;
    }

    &.development {
      background-color: #e04761;
      color: #fff;
    }

    &.stage {
      background-color: #237190;
      color: #fff;
    }
  }

  .snooze-dropdown {
    position: absolute;
    right: 5px;
    top: 3px;
    z-index: 1000;
  }
`;